/**
 * Signatures and Notary comment text generation.
 *
 * Kept free of component imports so the wording can be unit-tested directly.
 *
 * The section carries ONE set of toggles describing the report as a whole. When it covers several
 * G702s an extra "All Applications?" toggle selects between the plural wording (every application
 * in the stated condition) and the "with the exception of" wording. The excepted application is a
 * literal placeholder the reviewer fills in — it is deliberately not derived, because whether a
 * contractor signed is a human observation rather than something present in the data.
 */

/** Literal placeholder the reviewer replaces with the excepted application's name. */
export const EXCEPTION_PLACEHOLDER = '?????';

export type AllApplications = 'All' | 'Some';

// ─── Contractor signature ─────────────────────────────────────────────────────

export interface ContractorSignatureInput {
  /** False for a single-G702 report, which keeps the original singular wording. */
  isMultiDraw: boolean;
  allApplications: AllApplications;
  signed: boolean;
  notarized: boolean;
}

const CONTRACTOR_LENDER_PLURAL =
  'We leave the signature requirements as a condition for funding the Applications to the discretion of the Lender.';
/** The exception branches use the singular "Application", per spec. */
const CONTRACTOR_LENDER_SINGULAR =
  'We leave the signature requirements as a condition for funding the Application to the discretion of the Lender.';

/** Single-G702 wording, unchanged from before multi-draw support. */
const buildSingleDrawContractorComment = ({ signed, notarized }: ContractorSignatureInput): string => {
  if (notarized) {
    return 'The Application for Payment has been signed and dated by the Contractor and notarized as required.';
  }
  if (signed) {
    return `The Application for Payment has been signed by the Contractors but not notarized. ${CONTRACTOR_LENDER_PLURAL}`;
  }
  return `The Application for Payment has not been signed or notarized by the Contractor. ${CONTRACTOR_LENDER_PLURAL}`;
};

export const buildContractorSignatureComment = (input: ContractorSignatureInput): string => {
  const { isMultiDraw, allApplications, signed, notarized } = input;
  if (!isMultiDraw) return buildSingleDrawContractorComment(input);

  if (allApplications === 'All') {
    if (signed && notarized) {
      return 'The Applications for Payment have been signed and dated by the Contractors and notarized as required.';
    }
    if (signed) {
      return `The Applications for Payment have been signed by the Contractors but not notarized. ${CONTRACTOR_LENDER_PLURAL}`;
    }
    return `The Applications for Payment have not been signed or notarized by the Contractors. ${CONTRACTOR_LENDER_PLURAL}`;
  }

  // Some: the applications generally comply; the toggles describe the excepted one.
  if (signed && notarized) {
    return `The Applications for Payment have been signed and dated by the Contractors and notarized as required with the exception of the ${EXCEPTION_PLACEHOLDER} Application. ${CONTRACTOR_LENDER_SINGULAR}`;
  }
  if (signed) {
    return `The Applications for Payment have been signed and notarized by the Contractors with the exception of the ${EXCEPTION_PLACEHOLDER} Application which was not notarized. ${CONTRACTOR_LENDER_SINGULAR}`;
  }
  return `The Applications for Payment have been signed and notarized by the Contractors with the exception of the ${EXCEPTION_PLACEHOLDER} Application. ${CONTRACTOR_LENDER_SINGULAR}`;
};

// ─── Architect / Engineer signature ───────────────────────────────────────────

export type ArchitectReviewState = 'reviewing' | 'notReviewing' | 'unsure';

export interface ArchEngSignatureInput {
  /** False for a single-G702 report, which keeps the original singular wording. */
  isMultiDraw: boolean;
  allApplications: AllApplications;
  review: ArchitectReviewState;
  /** The "Signed" toggle — only meaningful while the architect is reviewing. */
  certified: boolean;
}

const ARCH_NOT_REVIEWING =
  "We understand that the Architect of Record is not reviewing the Applications for Payment from the Contractor. We recommend that they do so in order to provide subsequent quality control to that provided by the Contractor. The funding of each Application without Certification by the Architect is left to the Lender's discretion.";

const ARCH_UNSURE_ALL =
  "We are unaware if the Architect of Record is, or is not, reviewing the Applications for Payment from the Contractor, or is providing Construction Administration services to fulfill their contractual obligations to the Borrower and/or confirm the quality and strict compliance of the Work to the Plans and Specifications. The funding of the current Applications without Certification by the Architect is left to the Lender's discretion.";

const ARCH_CERTIFICATION_LENDER =
  'We leave the Certification requirements as a condition for funding the Applications to the discretion of the Lender.';

const ARCH_CERTIFIED_WITH_EXCEPTION = `The Architect of Record has certified the Amount of the Contractors' current Applications with the exception of the ${EXCEPTION_PLACEHOLDER} Application.`;

/** Single-G702 wording, unchanged from before multi-draw support. */
const buildSingleDrawArchEngComment = ({ review, certified }: ArchEngSignatureInput): string => {
  if (review === 'reviewing' && certified) {
    return "The Architect of Record has certified the Amount of the Contractor's current Application.";
  }
  if (review === 'reviewing') {
    return 'We understand that the Architect of Record is reviewing the Applications for Payment, but we have not received a copy of the certified document. We leave the Certification requirements as a condition for funding the Application to the discretion of the Lender.';
  }
  if (review === 'unsure') {
    return "We are unaware if the Architect of Record is, or is not, reviewing the Application for Payment from the Contractor, or is providing Construction Administration services to fulfill their contractual obligations to the Borrower and/or confirm the quality and strict compliance of the Work to the Plans and Specifications. The funding of the current Application without Certification by the Architect is left to the Lender's discretion.";
  }
  return ARCH_NOT_REVIEWING;
};

export const buildArchEngSignatureComment = (input: ArchEngSignatureInput): string => {
  const { isMultiDraw, allApplications, review, certified } = input;
  if (!isMultiDraw) return buildSingleDrawArchEngComment(input);

  if (allApplications === 'All') {
    if (review === 'notReviewing') return ARCH_NOT_REVIEWING;
    if (review === 'unsure') return ARCH_UNSURE_ALL;
    if (certified) return "The Architect of Record has certified the Amount of the Contractors' current Applications.";
    return `We understand that the Architect of Record is reviewing the Applications for Payment, but we have not received a copy of the certified documents. ${ARCH_CERTIFICATION_LENDER}`;
  }

  // Some: the applications are generally certified; the toggles describe the excepted one.
  // Note: per spec, "not reviewing" reads identically to the All variant — it names no exception.
  if (review === 'notReviewing') return ARCH_NOT_REVIEWING;

  if (review === 'unsure') {
    return `${ARCH_CERTIFIED_WITH_EXCEPTION} On this one we are unaware if the Architect of Record is, or is not, reviewing the Applications for Payment from the Contractor, or is providing Construction Administration services to fulfill their contractual obligations to the Borrower and/or confirm the quality and strict compliance of the Work to the Plans and Specifications. The funding of the current Applications without Certification by the Architect is left to the Lender's discretion.`;
  }

  if (certified) {
    return `${ARCH_CERTIFIED_WITH_EXCEPTION} ${ARCH_CERTIFICATION_LENDER}`;
  }

  return `${ARCH_CERTIFIED_WITH_EXCEPTION} On this one we understand that the Architect of Record is reviewing the Applications for Payment, but we have not received a copy of the certified documents. ${ARCH_CERTIFICATION_LENDER}`;
};
